"use client";

/**
 * Client body for the /security marketing page — the destination the nav's
 * Security mega-menu deep-links into (#tenant-isolation, #governance,
 * #evidence, #auditability).
 *
 * The page states how the governance promise is enforced, in four pillars
 * (isolation, governance, evidence, auditability), then the deliberate
 * limits and the baseline platform practices. Every claim maps to documented
 * shipped behaviour (content/docs/en/admin/security.md, the decision ledger,
 * the outbox) — see docs/ai/fixcontrol-website-claims.md before editing copy.
 * All copy lives in the `securityPage` namespace (EN + NL in lockstep).
 */

import {
  Activity,
  Ban,
  Boxes,
  BadgeCheck,
  Bot,
  CalendarClock,
  ClipboardList,
  Database,
  Eye,
  FileDown,
  FileSearch,
  FileSignature,
  Fingerprint,
  FlaskConical,
  GitBranch,
  Globe,
  HardDrive,
  History,
  KeyRound,
  Layers,
  Link2,
  Lock,
  MailWarning,
  Radio,
  ScrollText,
  Send,
  Server,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserCheck,
  Users,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  CtaBanner,
  Faq,
  FeatureGrid,
  RelatedLinks,
  Section,
  SectionHeader,
  UseCaseHero,
} from "@/components/marketing/marketing-kit";
import { Reveal } from "@/components/reveal";
import { useLocale, useT } from "@/i18n";

export function SecurityContent() {
  const t = useT();
  const { locale } = useLocale();
  const k = (s: string) => `securityPage.${s}`;
  const docsHref = `/docs/${locale}/admin/security`;

  const feature = (ns: string, key: string, icon: React.ReactNode) => ({
    icon,
    title: t(k(`${ns}.items.${key}.title`)),
    body: t(k(`${ns}.items.${key}.body`)),
  });

  const glance = [
    { key: "encryption", icon: <Lock size={13} /> },
    { key: "rls", icon: <Boxes size={13} /> },
    { key: "audit", icon: <Link2 size={13} /> },
    { key: "identity", icon: <Fingerprint size={13} /> },
    { key: "twoPerson", icon: <Users size={13} /> },
  ] as const;

  const ladder = ["suggested", "draft", "auto"] as const;

  // AI-boundaries columns: may-do / waits-for-approval / never-promised.
  const boundaryGroups = [
    { key: "auto", items: ["classify", "answer", "draft", "analyze", "checks", "environment"] },
    { key: "gated", items: ["replies", "tracker", "pr", "deploy", "writes"] },
    { key: "never", items: ["hallucination", "replacement", "liability"] },
  ] as const;

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: docsHref, label: t(k("hero.primaryCta")) }}
        secondary={{ href: "/contact", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      {/* At-a-glance strip — the hard numbers behind the promise. */}
      <div className="border-b border-line-subtle bg-bg-raised/25">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-7 gap-y-2 px-5 py-5 text-[13px] text-fg-dim sm:px-6">
          {glance.map((g) => (
            <span key={g.key} className="inline-flex items-center gap-1.5">
              <span className="text-fg-muted">{g.icon}</span>
              {t(k(`glance.${g.key}`))}
            </span>
          ))}
        </div>
      </div>

      {/* ── Tenant isolation ──────────────────────────────────────────────── */}
      <Section id="tenant-isolation">
        <SectionHeader
          align="left"
          eyebrow={t(k("isolation.eyebrow"))}
          title={t(k("isolation.title"))}
          description={t(k("isolation.description"))}
        />
        <FeatureGrid
          columns={2}
          features={[
            feature("isolation", "layers", <Boxes size={18} />),
            feature("isolation", "url", <Globe size={18} />),
            feature("isolation", "secrets", <KeyRound size={18} />),
            feature("isolation", "staff", <Eye size={18} />),
          ]}
        />
      </Section>

      {/* ── Governance ────────────────────────────────────────────────────── */}
      <Section id="governance" muted>
        <SectionHeader
          align="left"
          eyebrow={t(k("governance.eyebrow"))}
          title={t(k("governance.title"))}
          description={t(k("governance.description"))}
        />
        <FeatureGrid
          columns={2}
          features={[
            feature("governance", "approval", <UserCheck size={18} />),
            feature("governance", "ledger", <ScrollText size={18} />),
            feature("governance", "twoPerson", <Users size={18} />),
            feature("governance", "outbox", <Send size={18} />),
          ]}
        />

        {/* Staged-autonomy ladder — suggested → auto-draft → auto-apply. */}
        <div className="mx-auto mt-14 max-w-3xl">
          <Reveal>
            <div className="text-center text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted">
              {t(k("governance.ladder.title"))}
            </div>
          </Reveal>
          <ol className="mt-6 divide-y divide-line-subtle rounded-2xl border border-line-subtle bg-bg-raised">
            {ladder.map((step, i) => (
              <Reveal key={step} delay={i * 60} as="li">
                <div className="flex items-start gap-4 px-5 py-5 sm:px-6">
                  <span className="mt-0.5 text-[12px] font-mono text-fg-muted">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-[15px] font-semibold text-fg-bright leading-snug">
                        {t(k(`governance.ladder.steps.${step}.title`))}
                      </h3>
                      <span className="rounded-full border border-line-subtle bg-bg-elev px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
                        {t(k(`governance.ladder.steps.${step}.tag`))}
                      </span>
                    </div>
                    <p className="mt-1.5 text-[14px] text-fg-dim leading-relaxed">
                      {t(k(`governance.ladder.steps.${step}.body`))}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </Section>

      {/* ── Evidence ──────────────────────────────────────────────────────── */}
      <Section id="evidence">
        <SectionHeader
          align="left"
          eyebrow={t(k("evidence.eyebrow"))}
          title={t(k("evidence.title"))}
          description={t(k("evidence.description"))}
        />
        <FeatureGrid
          columns={2}
          features={[
            feature("evidence", "explainable", <FileSearch size={18} />),
            feature("evidence", "sandbox", <FlaskConical size={18} />),
            feature("evidence", "lineage", <GitBranch size={18} />),
            feature("evidence", "health", <Activity size={18} />),
          ]}
        />
      </Section>

      {/* ── Auditability ──────────────────────────────────────────────────── */}
      <Section id="auditability" muted>
        <SectionHeader
          align="left"
          eyebrow={t(k("audit.eyebrow"))}
          title={t(k("audit.title"))}
          description={t(k("audit.description"))}
        />
        <FeatureGrid
          columns={2}
          features={[
            feature("audit", "chain", <Link2 size={18} />),
            feature("audit", "export", <FileDown size={18} />),
            feature("audit", "replay", <History size={18} />),
            feature("audit", "retention", <CalendarClock size={18} />),
          ]}
        />
      </Section>

      {/* ── AI boundaries — may / waits / never-promised ──────────────────── */}
      <Section id="ai-boundaries">
        <SectionHeader
          align="left"
          eyebrow={t(k("boundaries.eyebrow"))}
          title={t(k("boundaries.title"))}
          description={t(k("boundaries.description"))}
        />
        <div className="grid gap-4 md:grid-cols-3">
          {boundaryGroups.map((group, gi) => (
            <Reveal key={group.key} delay={gi * 60}>
              <div className="card h-full p-5 sm:p-6">
                <span className="rounded-full border border-line-subtle bg-bg-elev px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
                  {t(k(`boundaries.groups.${group.key}.tag`))}
                </span>
                <h3 className="mt-2.5 text-[15px] font-semibold text-fg-bright leading-snug">
                  {t(k(`boundaries.groups.${group.key}.title`))}
                </h3>
                <ul className="mt-3.5 space-y-2.5">
                  {group.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-[13.5px] text-fg-dim leading-relaxed">
                      <span aria-hidden className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-fg-muted" />
                      {t(k(`boundaries.groups.${group.key}.items.${item}`))}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ── Compliance & assurance — honest certification status ──────────── */}
      <Section id="compliance" muted>
        <SectionHeader
          align="left"
          eyebrow={t(k("compliance.eyebrow"))}
          title={t(k("compliance.title"))}
          description={t(k("compliance.description"))}
        />
        <FeatureGrid
          columns={2}
          features={[
            feature("compliance", "certifications", <BadgeCheck size={18} />),
            feature("compliance", "pentest", <Bot size={18} />),
            feature("compliance", "disclosure", <MailWarning size={18} />),
            feature("compliance", "procurement", <ClipboardList size={18} />),
            feature("compliance", "dpa", <FileSignature size={18} />),
            feature("compliance", "onboarding", <Layers size={18} />),
          ]}
        />
      </Section>

      {/* ── Hosting, data & AI providers ──────────────────────────────────── */}
      <Section id="hosting">
        <SectionHeader
          align="left"
          eyebrow={t(k("platform.eyebrow"))}
          title={t(k("platform.title"))}
          description={t(k("platform.description"))}
        />
        <FeatureGrid
          columns={2}
          features={[
            feature("platform", "hosting", <Server size={18} />),
            feature("platform", "aiProviders", <Sparkles size={18} />),
            feature("platform", "training", <Ban size={18} />),
            feature("platform", "subprocessors", <Boxes size={18} />),
            feature("platform", "keys", <KeyRound size={18} />),
            feature("platform", "accounts", <Fingerprint size={18} />),
            feature("platform", "database", <Database size={18} />),
            feature("platform", "privateInfra", <Radio size={18} />),
            feature("platform", "backups", <HardDrive size={18} />),
            feature("platform", "deletion", <Trash2 size={18} />),
          ]}
        />
      </Section>

      {/* ── Deliberate limits ─────────────────────────────────────────────── */}
      <Section muted>
        <SectionHeader
          eyebrow={t(k("limits.eyebrow"))}
          title={t(k("limits.title"))}
          description={t(k("limits.description"))}
        />
        <FeatureGrid
          columns={2}
          features={[
            feature("limits", "automerge", <Ban size={18} />),
            feature("limits", "defaults", <ShieldCheck size={18} />),
            feature("limits", "crossTenant", <Boxes size={18} />),
            feature("limits", "mfa", <Fingerprint size={18} />),
          ]}
        />
      </Section>

      {/* ── Platform practices ────────────────────────────────────────────── */}
      <Section>
        <SectionHeader
          eyebrow={t(k("practices.eyebrow"))}
          title={t(k("practices.title"))}
          description={t(k("practices.description"))}
        />
        <FeatureGrid
          features={[
            feature("practices", "tls", <Globe size={18} />),
            feature("practices", "secrets", <Lock size={18} />),
            feature("practices", "sessions", <History size={18} />),
            feature("practices", "identity", <Fingerprint size={18} />),
            feature("practices", "passwords", <KeyRound size={18} />),
            feature("practices", "webhooks", <Send size={18} />),
          ]}
        />
      </Section>

      {/* ── FAQ (mirrored in the page's FAQPage JSON-LD) ──────────────────── */}
      <Section muted>
        <SectionHeader title={t(k("faqTitle"))} />
        <Faq
          items={(["q1", "q2", "q3", "q4", "q5", "q6", "q7", "q8", "q9"] as const).map((id) => ({
            q: t(k(`faq.${id}.q`)),
            a: t(k(`faq.${id}.a`)),
          }))}
        />
      </Section>

      <Section>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/integrations/fc-agent",
              label: t(k("related.agent.label")),
              description: t(k("related.agent.description")),
            },
            {
              href: "/product/change-validation",
              label: t(k("related.validation.label")),
              description: t(k("related.validation.description")),
            },
            {
              href: "/use-cases/ai-deployment-governance",
              label: t(k("related.deployment.label")),
              description: t(k("related.deployment.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: docsHref, label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
